import { Injectable, inject } from '@angular/core';

import { CookieName } from '../browser/cookie/cookie.models';
import { CookieService } from '../browser/cookie/cookie.service';
import { WINDOW } from '../browser/window/window.token';
import { NativeAppService } from '../native-app/native-app.service';
import { DeviceFingerPrint } from './login.models';

/**
 * @stable
 */
@Injectable({
    providedIn: 'root',
})
export class DeviceFingerprintService {
    private readonly window = inject(WINDOW);
    private readonly cookieService = inject(CookieService);
    private readonly nativeAppService = inject(NativeAppService);

    get(): DeviceFingerPrint {
        const deviceFingerPrint = {
            superCookie: this.cookieService.get(CookieName.SuperCookie),
            deviceDetails: this.getBrowserDeviceDetails(),
        };

        try {
            this.enrichDeviceDetailsWithCookie(deviceFingerPrint);
        } catch {}

        return deviceFingerPrint;
    }

    storeDeviceDetails(deviceDetails: { [key: string]: string }) {
        const browserDeviceDetails = this.getBrowserDeviceDetails();
        const oldDeviceDetails = this.cookieService.get(CookieName.DeviceDetails);
        const parsedOldDeviceDetails = oldDeviceDetails ? JSON.parse(oldDeviceDetails) : {};

        this.cookieService.put(CookieName.DeviceDetails, JSON.stringify({ ...parsedOldDeviceDetails, ...browserDeviceDetails, ...deviceDetails }), {
            expires: this.getCookieExpirationDate(),
        });
    }

    private enrichDeviceDetailsWithCookie(deviceFingerPrint: DeviceFingerPrint) {
        const deviceDetails = this.cookieService.get(CookieName.DeviceDetails);
        const parsedDeviceDetails = deviceDetails ? JSON.parse(deviceDetails) : deviceDetails;

        if (parsedDeviceDetails) {
            Object.assign(deviceFingerPrint.deviceDetails || {}, parsedDeviceDetails);
        }
    }

    private static getTimezone(): string {
        let timezone = '';

        try {
            // attempt one: use Intl
            timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch {
            try {
                // extract W. Europe Daylight Time from Thu Mar 29 2018 10:03:56 GMT+0200 (W. Europe Daylight Time)
                timezone = new Date().toString().split('(')[1]!.slice(0, -1);
            } catch {}
        }

        return timezone;
    }

    private getBrowserDeviceDetails(): { [key: string]: string } {
        return {
            sr: `${this.window.screen.width}, ${this.window.screen.height}|${this.window.screen.availWidth}, ${this.window.screen.availHeight}|${this.window.screen.colorDepth}`,
            sse: this.window.sessionStorage ? '1' : '0',
            lse: this.window.localStorage ? '1' : '0',
            idbe: this.window.indexedDB ? '1' : '0',
            hc: `${this.window.navigator.hardwareConcurrency ?? ''}`,
            bl: this.window.navigator.language.toString(),
            host: this.window.location.hostname.toString(),
            ua: this.window.navigator.userAgent,
            tz: DeviceFingerprintService.getTimezone(),
            dt: this.nativeAppService.isNative ? this.nativeAppService.applicationName : 'mobileweb',
            ct: this.window.navigator.userAgent.indexOf('x64') > -1 ? 'x64' : 'x32',
            dnt: '',
        };
    }

    private getCookieExpirationDate(): Date {
        const expires = new Date();
        expires.setFullYear(expires.getFullYear() + 1);

        return expires;
    }
}
